import { z } from 'zod';
export const planTierSchema = z.enum(['free', 'basic', 'pro']);
/**
 * Catálogo único dos planos: nome, preço (em centavos) e o que cada um libera.
 * Servidor, app e landing leem daqui; não existe outro lugar com esses números.
 * As chaves internas (free/basic/pro) não mudam; os nomes de venda são
 * Essencial, Pessoal e Completo.
 */
export const PLAN_CATALOG = {
    free: {
        name: 'Essencial',
        price: 0,
        introPrice: null,
        annualPrice: null,
        trialDays: 0,
        aiDailyLimit: 3,
        aiMonthlyLimit: 10,
        supervisedProfiles: 0,
        voiceMinutesMonthly: 0,
        voiceSampleMinutes: 3,
        deliveriesMonthly: 0,
        deliverySample: 1,
        savedFilesMb: 100,
        documentExtras: false,
    },
    basic: {
        name: 'Pessoal',
        price: 3990,
        introPrice: null,
        annualPrice: 39900,
        trialDays: 14,
        aiDailyLimit: 40,
        aiMonthlyLimit: 400,
        // Fora do catálogo até existir o produto (estudo de 28/10: não vender o que não há).
        supervisedProfiles: 0,
        voiceMinutesMonthly: 30,
        voiceSampleMinutes: 0,
        deliveriesMonthly: 3,
        deliverySample: 0,
        savedFilesMb: 5120,
        documentExtras: true,
    },
    pro: {
        name: 'Completo',
        price: 11990,
        introPrice: null,
        annualPrice: 119900,
        trialDays: 0,
        aiDailyLimit: 120,
        aiMonthlyLimit: 1000,
        // Fora do catálogo até existir o produto (estudo de 28/10: não vender o que não há).
        supervisedProfiles: 0,
        voiceMinutesMonthly: 90,
        voiceSampleMinutes: 0,
        deliveriesMonthly: 15,
        deliverySample: 0,
        savedFilesMb: 25600,
        documentExtras: true,
    },
};
/**
 * Créditos extras: quando os pedidos do plano acabam, cada crédito vale um
 * pedido. Também pagam a pesquisa na internet e os minutos de conversa ao vivo
 * além do plano. Créditos comprados não expiram.
 */
export const CREDIT_COSTS = {
    /** Um pedido ao Secretário. */
    request: 1,
    /** Cada pesquisa na internet feita durante uma resposta. */
    webSearch: 3,
    /** Cada minuto (ou fração) de conversa ao vivo além do plano. */
    voiceMinute: 6,
};
/**
 * Teto durante o teste grátis: a pessoa conhece o plano inteiro, mas o uso do
 * teste fica limitado, para quem cancela antes de pagar não virar prejuízo.
 */
export const TRIAL_LIMITS = {
    aiMonthly: 100,
    voiceMinutes: 15,
    deliveries: 1,
};
/** Mensalidade equivalente do plano anual ("R$ 33,25"), em centavos. */
export function annualMonthly(plan) {
    const annual = PLAN_CATALOG[plan].annualPrice;
    return annual === null ? null : Math.round(annual / 12);
}
/** Meses de presente no anual em relação a pagar 12 mensalidades (2 = "dois meses de presente"). */
export function annualFreeMonths(plan) {
    const { price, annualPrice } = PLAN_CATALOG[plan];
    if (!annualPrice || !price)
        return 0;
    return Math.max(0, Math.round((price * 12 - annualPrice) / price));
}
export const creditPackIdSchema = z.enum(['creditos_100', 'creditos_300', 'creditos_1000']);
export const CREDIT_PACKS = {
    creditos_100: { credits: 100, price: 1490 },
    creditos_300: { credits: 300, price: 3990 },
    creditos_1000: { credits: 1000, price: 11990 },
};
/** Produto da loja (Google Play / App Store) de cada pacote: compra consumível. */
export const creditStoreProductId = (pack) => `secretario_${pack}`;
/** Pacote a partir do produto da loja ("secretario_creditos_300" → creditos_300). */
export function creditPackOfProduct(productId) {
    const match = /creditos_(\d+)/.exec(productId);
    const id = match ? `creditos_${match[1]}` : '';
    return creditPackIdSchema.safeParse(id).success ? id : null;
}
const PLAN_RANK = { free: 0, basic: 1, pro: 2 };
/** O plano da pessoa cobre o que exige o plano `minimum`? */
export function planAtLeast(plan, minimum) {
    return PLAN_RANK[plan] >= PLAN_RANK[minimum];
}
/** "R$ 119,90" a partir de centavos. */
export function formatPlanPrice(cents) {
    const whole = Math.floor(cents / 100);
    const fraction = String(cents % 100).padStart(2, '0');
    return `R$ ${whole.toLocaleString('pt-BR')},${fraction}`;
}
export const paidPlanSchema = z.enum(['basic', 'pro']);
/** Período da assinatura: mensal ou anual (pago de uma vez). */
export const billingIntervalSchema = z.enum(['month', 'year']);
/** Produto de assinatura de cada plano nas lojas (Google Play / App Store). */
export const PLAN_STORE_PRODUCT = {
    basic: 'secretario_pessoal',
    pro: 'secretario_completo',
};
/**
 * Plano a partir do produto da loja: pelo id exato ou pela palavra inteira
 * ("pessoal"/"completo", e os antigos "basic"/"pro"), nunca dentro de outra
 * palavra ("pro" em "product" não conta).
 */
export function planOfStoreProduct(productId) {
    const id = productId.trim().toLowerCase();
    if (!id)
        return null;
    if (id === PLAN_STORE_PRODUCT.pro)
        return 'pro';
    if (id === PLAN_STORE_PRODUCT.basic)
        return 'basic';
    const words = id.split(/[^a-z0-9]+/);
    if (words.includes('completo') || words.includes('pro'))
        return 'pro';
    if (words.includes('pessoal') || words.includes('basic'))
        return 'basic';
    return null;
}
/** O que cada canal de venda pode vender agora. */
export const channelSalesSchema = z.object({
    basic: z.boolean(),
    pro: z.boolean(),
    /** O plano anual também está à venda por este canal. */
    annual: z.boolean().optional(),
});
const NO_SALES = { basic: false, pro: false };
export const salesByChannelSchema = z.object({
    web: channelSalesSchema.default(NO_SALES),
    google_play: channelSalesSchema.default(NO_SALES),
    app_store: channelSalesSchema.default(NO_SALES),
});
export const entitlementStatusSchema = z.object({
    plan: planTierSchema,
    /** Preço histórico do Basic, mantido para as versões do app que ainda leem este campo. */
    commercialPrice: z.object({
        amount: z.literal(4990),
        currency: z.literal('BRL'),
        interval: z.literal('month'),
    }),
    /** Quais planos podem ser assinados agora por algum canal (web ou loja). */
    sales: channelSalesSchema.default(NO_SALES),
    /** O mesmo, canal por canal. */
    salesBy: salesByChannelSchema.default({
        web: NO_SALES,
        google_play: NO_SALES,
        app_store: NO_SALES,
    }),
    /** O preço de entrada do PRO (1º mês) vale para esta pessoa? Só para quem nunca assinou o PRO. */
    proIntroEligible: z.boolean().default(false),
    /** O teste grátis do Pessoal vale para esta pessoa? Só para quem nunca assinou nenhum plano. */
    trialEligible: z.boolean().default(false),
    /** Assinatura em vigor, quando existe. */
    subscription: z
        .object({
        plan: paidPlanSchema,
        source: z.enum(['web', 'google_play', 'app_store', 'grant']),
        /** Fim do período já pago (sem a folga de renovação). */
        renewsAt: z.iso.datetime().nullable(),
        cancelAtPeriodEnd: z.boolean(),
        /** Produto da loja ou do provedor web, quando conhecido. */
        productId: z.string().nullable().optional(),
        /** Fim real do período pago (igual a renewsAt; sem a folga de 2 dias). */
        periodEnd: z.iso.datetime().nullable().optional(),
        /** Mensal ou anual, quando o provedor informa. */
        interval: billingIntervalSchema.nullable().optional(),
        /** Fim do teste grátis, enquanto a pessoa está nele (o uso fica no teto do teste). */
        trialEndsAt: z.iso.datetime().nullable().optional(),
    })
        .nullable()
        .default(null),
    features: z.object({
        messaging: z.literal(true),
        ai: z.object({
            dailyLimit: z.number().int().positive().nullable(),
            usedToday: z.number().int().nonnegative(),
            remainingToday: z.number().int().nonnegative().nullable(),
            resetAt: z.iso.datetime(),
            /** Mensagens de hoje que ficaram sem resposta automática por falta de pedidos. */
            missedToday: z.number().int().nonnegative().default(0),
            /** Teto do mês (null = sem teto) e quanto já foi usado. Vale junto com o do dia. */
            monthlyLimit: z.number().int().positive().nullable().default(null),
            usedMonth: z.number().int().nonnegative().default(0),
            remainingMonth: z.number().int().nonnegative().nullable().default(null),
            monthResetAt: z.iso.datetime().nullable().default(null),
        }),
        /** Créditos extras comprados (não expiram). */
        credits: z
            .object({
            balance: z.number().int().nonnegative(),
            /** Dá para comprar créditos por este canal agora (web: Stripe configurado). */
            webSales: z.boolean(),
        })
            .default({ balance: 0, webSales: false }),
        supervisedProfiles: z.object({
            limit: z.number().int().nonnegative(),
        }),
        /** Conversa ao vivo: minutos do mês (contados em segundos) ou, no Essencial, a amostra única. */
        voice: z
            .object({
            enabled: z.boolean(),
            limitMinutes: z.number().int().nonnegative(),
            usedSeconds: z.number().int().nonnegative(),
            remainingSeconds: z.number().int().nonnegative(),
            resetAt: z.iso.datetime(),
            /** true = amostra única (não renova no mês). */
            sample: z.boolean().default(false),
        })
            .default({
            enabled: false,
            limitMinutes: 0,
            usedSeconds: 0,
            remainingSeconds: 0,
            resetAt: '1970-01-01T00:00:00.000Z',
            sample: false,
        }),
        /** Documentos prontos: por mês nos planos pagos; amostra única no Essencial. */
        documents: z
            .object({
            monthlyLimit: z.number().int().nonnegative().nullable(),
            used: z.number().int().nonnegative(),
            remaining: z.number().int().nonnegative().nullable(),
            /** true = amostra única (não renova no mês). */
            sample: z.boolean(),
        })
            .default({ monthlyLimit: null, used: 0, remaining: null, sample: false }),
        /** Início em palco (personagem, trilho e visões): liberada por pessoa ou para todos. */
        stage: z.object({ enabled: z.boolean() }).default({ enabled: false }),
    }),
});
